import { useEffect, useRef, useState } from "react";

// Maximum tilt in degrees. Subtle — premium, not gimmicky.
const MAX_TILT = 12;
const PARALLAX_DEPTH = 18; // px the image shifts on top of rotation

export default function TiltImage({ src, alt, "data-testid": testid }) {
  const wrapperRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, px: 0, py: 0, glareX: 50, glareY: 50, active: false });
  const [gyroState, setGyroState] = useState("idle"); // idle | prompting | granted | denied | unsupported
  const [orientationActive, setOrientationActive] = useState(false);

  // ── Desktop pointer tracking ───────────────────────────────────────────
  const handleMove = (e) => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width; // 0 → 1
    const y = (e.clientY - rect.top) / rect.height; // 0 → 1
    const ry = (x - 0.5) * 2 * MAX_TILT; // left = -, right = +
    const rx = -(y - 0.5) * 2 * MAX_TILT; // top = +, bottom = -
    const px = (x - 0.5) * PARALLAX_DEPTH;
    const py = (y - 0.5) * PARALLAX_DEPTH;
    setTilt({ rx, ry, px, py, glareX: x * 100, glareY: y * 100, active: true });
  };

  const handleLeave = () => {
    setTilt({ rx: 0, ry: 0, px: 0, py: 0, glareX: 50, glareY: 50, active: false });
  };

  // ── Mobile gyroscope tracking ──────────────────────────────────────────
  useEffect(() => {
    if (!orientationActive) return undefined;

    const onOrient = (e) => {
      // beta: front-to-back tilt (-180..180), gamma: left-to-right (-90..90)
      const beta = e.beta || 0;
      const gamma = e.gamma || 0;
      // Clamp into our tilt range; small multiplier so a gentle phone tilt is enough.
      const ry = Math.max(-MAX_TILT, Math.min(MAX_TILT, gamma * 0.6));
      const rx = Math.max(-MAX_TILT, Math.min(MAX_TILT, -((beta - 35) * 0.4)));
      const px = (ry / MAX_TILT) * PARALLAX_DEPTH * 0.5;
      const py = (-rx / MAX_TILT) * PARALLAX_DEPTH * 0.5;
      setTilt({
        rx,
        ry,
        px,
        py,
        glareX: 50 + (ry / MAX_TILT) * 30,
        glareY: 50 + (-rx / MAX_TILT) * 30,
        active: true,
      });
    };

    window.addEventListener("deviceorientation", onOrient);
    return () => window.removeEventListener("deviceorientation", onOrient);
  }, [orientationActive]);

  const requestGyro = async () => {
    // iOS 13+ requires explicit user permission.
    if (typeof window.DeviceOrientationEvent === "undefined") {
      setGyroState("unsupported");
      return;
    }
    if (typeof window.DeviceOrientationEvent.requestPermission === "function") {
      try {
        setGyroState("prompting");
        const res = await window.DeviceOrientationEvent.requestPermission();
        if (res === "granted") {
          setGyroState("granted");
          setOrientationActive(true);
        } else {
          setGyroState("denied");
        }
      } catch {
        setGyroState("denied");
      }
    } else {
      // Android + most browsers — no permission gate.
      setGyroState("granted");
      setOrientationActive(true);
    }
  };

  return (
    <div
      ref={wrapperRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      data-testid={testid}
      className="relative aspect-square bg-zinc-100 select-none overflow-hidden cursor-grab"
      style={{
        perspective: "1200px",
        transformStyle: "preserve-3d",
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) translate3d(${tilt.px}px, ${tilt.py}px, 0) scale(${tilt.active ? 1.04 : 1})`,
          transition: tilt.active ? "transform 60ms ease-out" : "transform 480ms cubic-bezier(0.22, 1, 0.36, 1)",
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="w-full h-full object-cover"
        />
        {/* Cursor-following gloss highlight — gives the depth a sheen. */}
        <div
          className="pointer-events-none absolute inset-0 mix-blend-overlay"
          style={{
            background: `radial-gradient(circle at ${tilt.glareX}% ${tilt.glareY}%, rgba(255,255,255,${tilt.active ? 0.35 : 0}) 0%, rgba(255,255,255,0) 55%)`,
            transition: tilt.active ? "background 60ms ease-out" : "background 480ms ease-out",
          }}
        />
        {/* Inner shadow for sculptural depth */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            boxShadow: "inset 0 0 60px rgba(0,0,0,0.12), inset 0 0 12px rgba(0,0,0,0.08)",
          }}
        />
      </div>

      {/* Hint chip — disappears after first interaction */}
      {!tilt.active && (
        <div
          className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/80 backdrop-blur text-[9px] uppercase tracking-[0.25em] font-bold luxe-text"
          data-testid="tilt-hint"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 blink-dot" />
          Move to Explore
        </div>
      )}

      {/* iOS gyroscope opt-in */}
      {gyroState === "idle" && (
        <button
          type="button"
          onClick={requestGyro}
          data-testid="enable-gyro-btn"
          className="absolute bottom-3 left-3 md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white text-[9px] uppercase tracking-[0.25em] font-bold"
          style={{ fontFamily: "Outfit" }}
        >
          Enable Gyroscope
        </button>
      )}
      {gyroState === "granted" && (
        <div className="absolute bottom-3 left-3 md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-700 text-white text-[9px] uppercase tracking-[0.25em] font-bold">
          Tilt to Explore
        </div>
      )}
      {(gyroState === "denied" || gyroState === "unsupported") && (
        <div className="absolute bottom-3 left-3 md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-200 text-zinc-700 text-[9px] uppercase tracking-[0.25em] font-bold">
          Touch to Tilt
        </div>
      )}
    </div>
  );
}
